import { useEffect, useState } from 'react';
import { Drawer, Badge, Empty, Tag, Spin } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  broadcastListMine,
  broadcastMarkRead,
  type MyBroadcastItem,
  type BroadcastPriority,
} from '@/services/broadcast';
import { useUserStore } from '@/stores/userStore';
import styles from './BroadcastInbox.module.css';

const PRIORITY_TAG: Record<BroadcastPriority, { color: string; label: string }> = {
  normal: { color: 'blue', label: '普通' },
  important: { color: 'orange', label: '重要' },
  critical: { color: 'red', label: '紧急' },
};

interface Props {
  /** 是否在深色头部里使用（影响图标颜色） */
  dark?: boolean;
}

function BroadcastInbox({ dark = false }: Props) {
  const isLoggedIn = useUserStore((s) => s.isLoggedIn);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MyBroadcastItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<MyBroadcastItem | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const list = await broadcastListMine();
      setItems(list);
      setUnreadCount(list.filter((i) => !i.readAt).length);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [isLoggedIn]);

  // 暴露给外部组件（弹窗关闭后刷新计数）
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('broadcast:refresh-inbox', handler);
    return () => window.removeEventListener('broadcast:refresh-inbox', handler);
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    refresh();
  };

  const handleSelect = async (item: MyBroadcastItem) => {
    setActive(item);
    if (!item.readAt) {
      try {
        await broadcastMarkRead(item.broadcast.id);
        setItems((prev) =>
          prev.map((i) =>
            i.broadcast.id === item.broadcast.id
              ? { ...i, readAt: new Date().toISOString() }
              : i,
          ),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    }
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <div
        className={`${styles.iconBtn} ${dark ? styles.iconBtnDark : styles.iconBtnLight}`}
        onClick={handleOpen}
        title="历史广播信箱"
      >
        <Badge count={unreadCount} size="small" offset={[0, 0]}>
          <BellOutlined style={{ fontSize: 18, color: 'inherit' }} />
        </Badge>
      </div>

      <Drawer
        rootClassName={dark ? styles.drawerDark : undefined}
        open={open}
        onClose={() => {
          setOpen(false);
          setActive(null);
        }}
        title={active ? active.broadcast.title : '历史广播信箱'}
        width={520}
        extra={
          active ? (
            <a onClick={() => setActive(null)}>返回列表</a>
          ) : null
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : active ? (
          <div>
            <div className={styles.detailMeta}>
              <Tag color={PRIORITY_TAG[active.broadcast.priority].color}>
                {PRIORITY_TAG[active.broadcast.priority].label}
              </Tag>
              <span style={{ marginLeft: 6 }}>
                {dayjs(active.broadcast.createdAt).format('YYYY-MM-DD HH:mm')}
              </span>
              {active.broadcast.senderName && (
                <span style={{ marginLeft: 6 }}>· 发自 {active.broadcast.senderName}</span>
              )}
            </div>
            <div className={styles.detailContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {active.broadcast.contentMd}
              </ReactMarkdown>
            </div>
          </div>
        ) : items.length === 0 ? (
          <Empty description="暂无广播" />
        ) : (
          items.map((it) => (
            <div
              key={it.broadcast.id}
              className={`${styles.item} ${!it.readAt ? styles.itemUnread : ''} ${
                it.broadcast.status === 'revoked' ? styles.itemRevoked : ''
              }`}
              onClick={() => handleSelect(it)}
            >
              <div className={styles.itemTitle}>
                <span>{it.broadcast.title}</span>
                <Tag
                  color={PRIORITY_TAG[it.broadcast.priority].color}
                  style={{ marginInlineEnd: 0 }}
                >
                  {PRIORITY_TAG[it.broadcast.priority].label}
                </Tag>
              </div>
              <div className={styles.itemMeta}>
                {dayjs(it.broadcast.createdAt).format('YYYY-MM-DD HH:mm')}
                {!it.readAt && (
                  <span style={{ marginLeft: 8, color: '#1677ff' }}>● 未读</span>
                )}
              </div>
            </div>
          ))
        )}
      </Drawer>
    </>
  );
}

export default BroadcastInbox;
